import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiFehler } from "../api/client";
import { useBetriebAnlegen, useBetriebe } from "../api/hooks";
import { Hinweis, Karte, Knopf, Modal, Spinner } from "../komponenten/Basis";
import { useToast } from "../kontext/ToastKontext";
import { datum } from "../lib/format";

/** Wird nur gerendert, solange es offen ist – so startet jedes Öffnen mit leeren Feldern. */
function NeuerBetriebModal({ schliessen }: { schliessen: () => void }) {
  const anlegen = useBetriebAnlegen();
  const toast = useToast();
  const navigate = useNavigate();
  const [werte, setWerte] = useState({ name: "", ansprechpartner: "", email: "", telefon: "", ort: "" });
  const [fehler, setFehler] = useState<string | null>(null);

  function setzen(feld: keyof typeof werte, wert: string) {
    setWerte((alt) => ({ ...alt, [feld]: wert }));
  }

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (werte.name.trim().length < 2) return setFehler("Bitte den Firmennamen eingeben.");
    anlegen.mutate(werte, {
      onSuccess: (betrieb) => {
        toast.erfolg("Betrieb angelegt", betrieb.name);
        schliessen();
        navigate(`/betriebe/${betrieb.id}`);
      },
      onError: (err) => setFehler(err instanceof ApiFehler ? err.message : "Anlegen fehlgeschlagen"),
    });
  }

  const feld = (name: keyof typeof werte, label: string, typ = "text") => (
    <div>
      <label htmlFor={`neu-${name}`} className="mb-1 block text-xs font-semibold">{label}</label>
      <input id={`neu-${name}`} type={typ} className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={werte[name]}
        onChange={(e) => setzen(name, e.target.value)} data-autofokus={name === "name" ? true : undefined} />
    </div>
  ); // prettier-ignore

  return (
    <Modal
      offen
      titel="Neuer Installationsbetrieb"
      beiSchliessen={schliessen}
      fuss={
        <>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf variante="primaer" type="submit" form="neuer-betrieb" laedt={anlegen.isPending}>Anlegen</Knopf>
        </>
      }
    >
      <form id="neuer-betrieb" onSubmit={absenden} className="space-y-4" noValidate>
        {feld("name", "Firmenname *")}
        <div className="grid gap-4 sm:grid-cols-2">
          {feld("ansprechpartner", "Ansprechpartner")}
          {feld("ort", "Ort")}
          {feld("email", "E-Mail (Firma)", "email")}
          {feld("telefon", "Telefon", "tel")}
        </div>
        <p className="text-xs text-neutral-500">Die Zugänge für die Mitarbeiter legst du im nächsten Schritt an.</p>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </form>
    </Modal>
  );
}

export function BetriebeSeite() {
  const abfrage = useBetriebe();
  const [neuOffen, setNeuOffen] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Installationsbetriebe</h1>
          <p className="mt-1 text-sm text-neutral-500">Kunden des Portals: Jeder Betrieb reicht seine Anmeldungen über eigene Zugänge ein.</p>
        </div>
        <Knopf variante="primaer" onClick={() => setNeuOffen(true)}>+ Neuer Betrieb</Knopf>
      </div>

      {abfrage.isLoading ? (
        <div className="flex justify-center py-16"><Spinner label="Betriebe werden geladen…" /></div>
      ) : abfrage.error ? (
        <Hinweis art="fehler">{abfrage.error.message}</Hinweis>
      ) : !abfrage.data?.length ? (
        <Karte className="p-8 text-center">
          <p className="font-semibold">Noch keine Betriebe</p>
          <p className="mt-1 text-sm text-neutral-500">Leg den ersten Installationsbetrieb an und lade seine Mitarbeiter ein.</p>
        </Karte>
      ) : (
        <Karte className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Betrieb</th>
                <th className="px-4 py-3">Ansprechpartner</th>
                <th className="px-4 py-3">Zugänge</th>
                <th className="px-4 py-3">Kunden</th>
                <th className="px-4 py-3">Seit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {abfrage.data.map((b) => (
                <tr key={b.id} className={`hover:bg-neutral-50 ${b.aktiv ? "" : "text-neutral-400"}`}>
                  <td className="px-4 py-3">
                    <Link to={`/betriebe/${b.id}`} className="font-semibold underline-offset-2 hover:underline">{b.name}</Link>
                    {!b.aktiv && <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase">gesperrt</span>}
                    {b.ort && <span className="block text-xs text-neutral-500">{b.ort}</span>}
                  </td>
                  <td className="px-4 py-3">{b.ansprechpartner ?? "–"}</td>
                  <td className="px-4 py-3">{b.benutzer_anzahl}</td>
                  <td className="px-4 py-3">{b.kunden_anzahl}</td>
                  <td className="px-4 py-3 text-neutral-500">{datum(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Karte>
      )}

      {neuOffen && <NeuerBetriebModal schliessen={() => setNeuOffen(false)} />}
    </div>
  );
}
